// import axios from "axios";

// const api = axios.create({
//   baseURL: "http://localhost:8000/api/", // adjust to your Django host
//   withCredentials: true, // for session cookie auth
// });

// // Redirect to login on 403/401
// api.interceptors.response.use(
//   (r) => r,
//   (error) => {
//     if (error.response?.status === 401 || error.response?.status === 403) {
//       window.location.href = "/login";
//     }
//     return Promise.reject(error);
//   }
// );

// export default api;


// axios instance with interceptors

// src/api/client.ts
import { refreshToken } from "@/auth/auth";
import { createSessionExpiredError, emitSessionExpired } from "@/auth/sessionEvents";
import { useToastStore } from "@/ui/toastStore";
import axios from "axios";

const NETWORK_RETRY_DELAY_MS = 750;

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
//   baseURL: "http://127.0.0.1:8000/api",
//   baseURL: "https://pure.tmadvances.com/api",
  withCredentials: true,
});

// In-memory access token
let accessToken: string | null = null;
const apiBaseUrl = resolveApiBaseUrl();

export const setAccessToken = (token: string | null) => {
  accessToken = token;
};
export const getAccessToken = () => accessToken;

function ensureNoTrailingSlash(value: string) {
  return value.replace(/\/+$/, "");
}

function isAbsoluteUrl(value: string) {
  return /^https?:\/\//i.test(value);
}

function resolveApiBaseUrl() {
  const baseUrl = import.meta.env.VITE_API_BASE_URL;
  if (!baseUrl) return null;

  try {
    const origin = typeof window !== "undefined" ? window.location.origin : "http://localhost";
    return new URL(baseUrl, origin);
  } catch {
    return null;
  }
}

function resolveRequestUrl(url: string | undefined, baseURL?: string) {
  if (!url) return null;

  try {
    if (isAbsoluteUrl(url)) {
      return new URL(url);
    }

    const origin = typeof window !== "undefined" ? window.location.origin : "http://localhost";
    const effectiveBase = baseURL ?? import.meta.env.VITE_API_BASE_URL;
    if (!effectiveBase) {
      return new URL(url, origin);
    }

    const absoluteBase = new URL(effectiveBase, origin);
    const joined = `${ensureNoTrailingSlash(absoluteBase.toString())}/${url.replace(/^\/+/, "")}`;
    return new URL(joined);
  } catch {
    return null;
  }
}

function isManagedApiRequest(url: URL | null) {
  if (!url || !apiBaseUrl) return false;

  const apiPath = ensureNoTrailingSlash(apiBaseUrl.pathname);
  return (
    url.origin === apiBaseUrl.origin &&
    (url.pathname === apiPath || url.pathname.startsWith(`${apiPath}/`))
  );
}

function shouldAttemptRefresh(error: unknown) {
  const axiosError = error as {
    response?: { status?: number };
    config?: { url?: string; baseURL?: string; _retry?: boolean };
  };
  const original = axiosError.config;

  if (axiosError.response?.status !== 401 || original?._retry) {
    return false;
  }

  const requestUrl = resolveRequestUrl(original?.url, original?.baseURL);
  if (!isManagedApiRequest(requestUrl)) {
    return false;
  }

  const pathname = requestUrl?.pathname.toLowerCase() ?? "";
  return !pathname.includes("/auth/login/") && !pathname.includes("/auth/refresh/");
}

// attach access token
api.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

// auto-refresh on 401 + global error toast
const toast = useToastStore.getState().push;
let lastToast: { msg: string; at: number } | null = null;

function shouldIgnoreGlobalToast(url: string | undefined) {
  if (!url) return false;
  const lower = url.toLowerCase();
  return (
    lower.includes("manifest.json") ||
    lower.includes("/me/") ||
    lower.includes("/auth/refresh")
  );
}

function showToastOnce(msg: string, severity: "error" | "warning" | "info" | "success" = "error") {
  const now = Date.now();
  if (lastToast && lastToast.msg === msg && now - lastToast.at < 3000) return;
  lastToast = { msg, at: now };
  toast(msg, severity);
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;

    if (original?.url?.includes("auth/refresh")) {
      return Promise.reject(error);
    }

    if (shouldAttemptRefresh(error) && original) {
      original._retry = true;

      try {
        await refreshToken();

        // Re-attach token if we have one after refresh
        if (accessToken) {
          original.headers = original.headers ?? {};
          original.headers.Authorization = `Bearer ${accessToken}`;
        }

        return api(original); // retry original request
      } catch (err) {
        accessToken = null;
        emitSessionExpired();
        return Promise.reject(createSessionExpiredError(err));
      }
    }

    const shouldRetryNetworkOnce =
      !error.response &&
      error.code !== "ERR_CANCELED" &&
      !!original?.retryNetworkOnce &&
      !original?._networkRetry;

    if (shouldRetryNetworkOnce && original) {
      original._networkRetry = true;
      await sleep(NETWORK_RETRY_DELAY_MS);
      return api(original);
    }

    // Global error toast (unless suppressed)
    const suppress = original?.suppressGlobalErrorToast;
    const url = original?.url || "";
    if (!suppress && !shouldIgnoreGlobalToast(url)) {
      if (!error.response) {
        showToastOnce("Can't reach the server. Check your connection.");
      } else {
        const status = error.response.status;
        const detail =
          (error.response.data && (error.response.data.detail || error.response.data.error)) ||
          error.message ||
          "Something went wrong";
        if (status >= 500) {
          showToastOnce(`Server error (${status}). Please try again.`);
        } else if (status === 401 || status === 403) {
          // Skip noisy auth toasts by default; handled elsewhere
        } else {
          showToastOnce(detail);
        }
      }
    }

    return Promise.reject(error);
  }
);

export default api;
